// src/shared/stores/notificationsStore.ts

import { PiniaPluginOptions } from '@/plugins/pinia';
import { loggingService } from '@/services/logging.service';
import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
import { NotificationSeverity } from '@/types/ui/notifications';
import { AxiosInstance } from 'axios';
import { defineStore, PiniaCustomProperties, storeToRefs } from 'pinia';
import { inject, ref } from 'vue';

export type NotificationPosition = 'top' | 'bottom';

interface StoreOptions extends PiniaPluginOptions {}

/**
 * Type definition for NotificationsStore.
 */
export type NotificationsStore = {
  // State
  message: string;
  severity: NotificationSeverity;
  isVisible: boolean;
  position: NotificationPosition;
  _initialized: boolean;

  // Actions
  init: () => void;
  show: (msg: string, sev: 'success' | 'error' | 'info', pos?: NotificationPosition, duration?: number) => void;
  hide: () => void;
  $reset: () => void;
} & PiniaCustomProperties;

const DEFAULT_AUTO_HIDE_MS = 5000;

/**
 * Frontend notification management store integrated with server-side messages
 *
 * Handles application-wide notifications including success messages,
 * errors, and info alerts. Provides auto-dismissal and position control.
 *
 * Architecture:
 * - Initializes from server messages on mount
 * - Manages client-only state after initialization
 * - No direct coupling with SessionMessages module
 *
 * @example Initialize and handle server messages
 * ```ts
 * const store = useNotificationsStore();
 * store.init(); // Processes window.messages
 * ```
 *
 * @example Client-side notification
 * ```ts
 * store.show('Operation completed', 'success', 'top');
 * ```
 */

// eslint-disable-next-line max-lines-per-function
export const useNotificationsStore = defineStore('notifications', () => {
  const $api = inject('api') as AxiosInstance; // eslint-disable-line
  const bootstrapStore = useBootstrapStore();
  const { messages: bootstrapMessages } = storeToRefs(bootstrapStore);

  // State refs with default values
  const message = ref('');
  const severity = ref<NotificationSeverity>(null);
  const isVisible = ref(false);
  const position = ref<NotificationPosition>('top');
  const _initialized = ref(false);
  let _hideTimerId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Initialize notification store
   * @param options - Optional store configuration
   */
  function init(options?: StoreOptions) {
    if (_initialized.value) return;

    if (options?.api) {
      loggingService.warn('API instance provided in options, ignoring.');
    }

    const serverMessages = bootstrapMessages.value;
    if (!serverMessages?.length) return;

    // Get last error or info message
    const messages = [...serverMessages].reverse();
    const errorMessage = messages.find((msg) => msg.type === 'error');
    const successMessage = messages.find((msg) => msg.type === 'success');
    const infoMessage = messages.find((msg) => msg.type === 'info');

    // Display error message with priority over info
    if (errorMessage) {
      show(errorMessage.content, 'error');
    } else if (successMessage) {
      show(successMessage.content, 'success');
    } else if (infoMessage) {
      show(infoMessage.content, 'info');
    }

    _initialized.value = true;
  }

  /** Display a notification with auto-dismissal. Defaults to 5 s; pass 0 or negative to disable. */
  function show(msg: string, sev: NotificationSeverity, pos?: NotificationPosition, duration?: number) {
    // Clear any pending hide timer so earlier timeouts don't dismiss this message
    if (_hideTimerId !== null) {
      clearTimeout(_hideTimerId);
      _hideTimerId = null;
    }

    message.value = msg;
    severity.value = sev;
    position.value = pos || 'top';
    isVisible.value = true;

    const ms = duration ?? DEFAULT_AUTO_HIDE_MS;
    if (ms > 0) {
      _hideTimerId = setTimeout(() => {
        hide();
      }, ms);
    }
  }

  /** Hide the current notification and reset its state. */
  function hide() {
    if (_hideTimerId !== null) {
      clearTimeout(_hideTimerId);
      _hideTimerId = null;
    }
    isVisible.value = false;
    message.value = '';
    severity.value = null;
  }

  /** Reset store to initial state (clears message, severity, visibility, position). */
  function $reset() {
    if (_hideTimerId !== null) {
      clearTimeout(_hideTimerId);
      _hideTimerId = null;
    }
    message.value = '';
    severity.value = null;
    isVisible.value = false;
    position.value = 'top';
    _initialized.value = false;
  }

  return {
    init,

    // State
    message,
    severity,
    isVisible,
    position,
    _initialized,

    // Actions
    show,
    hide,
    $reset,
  };
});
